import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocCard,
  DocAlert,
  DocLink,
  DocNavFooter,
} from "@/components/docs";
import { CodeBlock } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import {
  KeyIcon,
  CubeTransparentIcon,
  BoltIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

export default function ApiOverviewPage() {
  const breadcrumbs = [{ label: "Documentation", href: "/docs" }, { label: "API Reference" }];

  return (
    <DocLayout
      title="API Reference - Uptime Monitor Documentation"
      description="Complete API reference for integrating Uptime Monitor with your applications"
      canonical="https://uptime-monitor.io/docs/api"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="API Reference"
        description="The Uptime Monitor API enables you to programmatically manage monitors, contacts, and incidents. Build powerful integrations and automate your monitoring workflows."
      />

      <DocAlert variant="info" className="mb-8">
        <p>
          <strong>Base URL:</strong>{" "}
          <code className="font-mono">https://api.uptime-monitor.io/api</code>
        </p>
        <p className="mt-2">
          All API requests must include authentication using Bearer tokens. See{" "}
          <DocLink href="/docs/api/authentication">Authentication</DocLink> for details.
        </p>
        <p className="mt-2">
          The API uses a point-based rate limiting system (5000 points/hour). See{" "}
          <DocLink href="/docs/api/rate-limiting">Rate Limiting</DocLink> for details.
        </p>
      </DocAlert>

      {/* Key Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <DocCard className="p-6">
          <div className="flex items-start">
            <KeyIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="ml-4">
              <h3 className={docsComponents.sectionHeading}>Simple Authentication</h3>
              <p className={docsColors.text.body}>
                Use API keys with Bearer authentication. Generate and manage keys directly from your
                account settings.
              </p>
            </div>
          </div>
        </DocCard>

        <DocCard className="p-6">
          <div className="flex items-start">
            <CubeTransparentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="ml-4">
              <h3 className={docsComponents.sectionHeading}>RESTful Design</h3>
              <p className={docsColors.text.body}>
                Standard HTTP methods and JSON request/response format. Easy to integrate with any
                programming language.
              </p>
            </div>
          </div>
        </DocCard>

        <DocCard className="p-6">
          <div className="flex items-start">
            <BoltIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="ml-4">
              <h3 className={docsComponents.sectionHeading}>Fast & Reliable</h3>
              <p className={docsColors.text.body}>
                Low latency responses with 99.9% uptime SLA. Rate limits ensure fair usage across
                all customers.
              </p>
            </div>
          </div>
        </DocCard>

        <DocCard className="p-6">
          <div className="flex items-start">
            <ShieldCheckIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="ml-4">
              <h3 className={docsComponents.sectionHeading}>Secure by Default</h3>
              <p className={docsColors.text.body}>
                All API traffic is encrypted with TLS 1.2+. API keys are account-scoped with
                granular permissions.
              </p>
            </div>
          </div>
        </DocCard>
      </div>

      {/* Quick Start */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>Quick Start</h2>

        <div className="space-y-6">
          <div>
            <h3 className={docsComponents.sectionHeading}>1. Generate an API Key</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Go to your{" "}
              <DocLink href="https://uptime-monitor.io/dashboard/settings" external>
                Account Settings
              </DocLink>{" "}
              and create a new API key in the API section. Store it securely - you won&apos;t be
              able to see it again.
            </p>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>2. Make Your First Request</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Test your API key by listing your monitors:
            </p>
            <CodeBlock language="bash">
              {`curl -X GET https://api.uptime-monitor.io/api/monitors \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`}
            </CodeBlock>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>3. Handle the Response</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              All successful responses return JSON with a <code>status</code> of{" "}
              <code>&ldquo;ok&rdquo;</code>:
            </p>
            <CodeBlock language="json">
              {`{
  "status": "ok",
  "monitors": [
    {
      "id": "507f1f77bcf86cd799439011",
      "name": "Production API",
      "url": "https://api.example.com",
      "type": "HTTPS",
      "interval": 60,
      "status": "ONLINE"
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 50,
    "total": 1,
    "total_pages": 1
  }
}`}
            </CodeBlock>
          </div>
        </div>
      </section>

      {/* Available Resources */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Available Resources
        </h2>

        <div className="space-y-6">
          <DocCard className="p-6">
            <h3 className={docsComponents.sectionHeading}>
              <DocLink href="/docs/api/contacts">Contacts API</DocLink>
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Manage notification channels including email, SMS, webhooks, Slack, Discord,
              PagerDuty, and more. Configure who gets alerted when monitors detect issues.
            </p>
            <div className={`${docsColors.text.muted} text-sm space-y-1`}>
              <div>
                <code className="text-xs">GET /api/contacts</code>
              </div>
              <div>
                <code className="text-xs">POST /api/contacts</code>
              </div>
              <div>
                <code className="text-xs">PUT /api/contacts/:id</code>
              </div>
              <div>
                <code className="text-xs">DELETE /api/contacts/:id</code>
              </div>
              <div>
                <code className="text-xs">POST /api/contacts/:id/reactivate</code>
              </div>
              <div>
                <code className="text-xs">POST /api/contacts/:id/test</code>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <h3 className={docsComponents.sectionHeading}>
              <DocLink href="/docs/api/monitors">Monitors API</DocLink>
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Create and manage HTTPS, TCP, and Ping monitors. Configure check intervals, regions,
              assertions, and notification rules.
            </p>
            <div className={`${docsColors.text.muted} text-sm space-y-1`}>
              <div>
                <code className="text-xs">GET /api/monitors</code>
              </div>
              <div>
                <code className="text-xs">POST /api/monitors</code>
              </div>
              <div>
                <code className="text-xs">PUT /api/monitors/:id</code>
              </div>
              <div>
                <code className="text-xs">DELETE /api/monitors/:id</code>
              </div>
              <div>
                <code className="text-xs">POST /api/monitors/:id/pause</code>
              </div>
              <div>
                <code className="text-xs">POST /api/monitors/:id/resume</code>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <h3 className={docsComponents.sectionHeading}>
              <DocLink href="/docs/api/incidents">Incidents API</DocLink>
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              View monitor incidents and downtime history.
            </p>
            <div className={`${docsColors.text.muted} text-sm space-y-1`}>
              <div>
                <code className="text-xs">GET /api/incidents</code>
              </div>
              <div>
                <code className="text-xs">GET /api/incidents/:id</code>
              </div>
              <div>
                <code className="text-xs">PUT /api/incidents/:id/comment</code>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <h3 className={docsComponents.sectionHeading}>
              <DocLink href="/docs/api/status-pages">Status Pages API</DocLink>
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Create and manage public status pages to share the health status of your monitored
              services with customers or team members.
            </p>
            <div className={`${docsColors.text.muted} text-sm space-y-1`}>
              <div>
                <code className="text-xs">GET /api/status_pages</code>
              </div>
              <div>
                <code className="text-xs">POST /api/status_pages</code>
              </div>
              <div>
                <code className="text-xs">PUT /api/status_pages/:id</code>
              </div>
              <div>
                <code className="text-xs">DELETE /api/status_pages/:id</code>
              </div>
            </div>
          </DocCard>
        </div>
      </section>

      {/* Response Format */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Response Format
        </h2>

        <p className={`${docsColors.text.body} mb-4`}>
          All API responses follow a consistent JSON format:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Success Response</h3>
            <CodeBlock language="json">
              {`{
  "status": "ok",
  "data": { ... },
  "pagination": { ... }
}`}
            </CodeBlock>
          </div>

          <div>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Error Response</h3>
            <CodeBlock language="json">
              {`{
  "status": "error",
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Invalid request data"
  }
}`}
            </CodeBlock>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Additional Resources
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DocLink
            href="/docs/api/rate-limiting"
            className={`block p-4 rounded-lg ${docsColors.bg.card} border ${docsColors.border.default} hover:border-blue-500 transition-colors`}
          >
            <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>Rate Limiting</h3>
            <p className={`text-sm ${docsColors.text.muted}`}>
              Learn about point-based rate limits and best practices for handling them
            </p>
          </DocLink>

          <DocLink
            href="/docs/api/authentication"
            className={`block p-4 rounded-lg ${docsColors.bg.card} border ${docsColors.border.default} hover:border-blue-500 transition-colors`}
          >
            <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
              Authentication Guide
            </h3>
            <p className={`text-sm ${docsColors.text.muted}`}>
              Detailed guide on API key generation and Bearer token authentication
            </p>
          </DocLink>
        </div>
      </section>

      <DocNavFooter
        prev={{ href: "/docs", text: "Documentation Home" }}
        next={{ href: "/docs/api/authentication", text: "Authentication" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
